import React, { useState, useEffect } from 'react';
import './DashboardStats.css';

const DashboardStats = ({ events }) => {
  const [selectedEventId, setSelectedEventId] = useState('all');
  // Set default to first event if available
  useEffect(() => {
    if (events.length > 0 && selectedEventId === 'all') {
      // Keep 'all' as default
    }
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter events based on selection
  const filteredEvents = selectedEventId === 'all' 
    ? events 
    : events.filter(e => e.id === selectedEventId);

  // Calculate comprehensive stats from filtered events
  const calculateStats = () => {
    let totalPaidContractors = 0;
    let totalFreeEmployees = 0;
    let totalWaitlisted = 0;
    let totalRevenue = 0;
    let totalPending = 0;
    let revenueByEvent = {};

    filteredEvents.forEach((event) => {
      revenueByEvent[event.title] = 0;
      
      if (event.rsvps) {
        event.rsvps.forEach((rsvp) => {
          // Paid contractors
          if (rsvp.registrationType === 'contractor' && rsvp.paymentStatus === 'paid') {
            totalPaidContractors++;
            const amount = parseFloat(rsvp.amountPaid || 0) / 100;
            totalRevenue += amount;
            revenueByEvent[event.title] += amount;
          }
          
          // Free employees (active only)
          if (rsvp.registrationType === 'employee' && rsvp.employeeStatus === 'active') {
            totalFreeEmployees++;
          }
          
          // Waitlisted employees
          if (rsvp.registrationType === 'employee' && rsvp.employeeStatus === 'waitlisted') {
            totalWaitlisted++;
          }
          
          // Pending payments
          if (rsvp.paymentStatus === 'pending') {
            totalPending++;
          }
        });
      }
    });

    return {
      totalPaidContractors,
      totalFreeEmployees,
      totalWaitlisted,
      totalRevenue,
      totalPending,
      totalAttending: totalPaidContractors + totalFreeEmployees,
      revenueByEvent
    };
  };

  const stats = calculateStats();

  return (
    <div className="dashboard-stats">
      <div className="stats-header">
        <h3>📊 Dashboard Overview</h3>
        <select 
          className="event-selector"
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
        >
          <option value="all">All Events</option>
          {events
            .filter(event => event.isActive)
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map((event) => (
              <option key={event.id} value={event.id}>
                {event.title} - {new Date(event.date).toLocaleDateString()}
              </option>
            ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">${stats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Confirmed</div>
            <div className="stat-value">{stats.totalAttending}</div>
            <div className="stat-detail">{stats.totalPaidContractors} paid • {stats.totalFreeEmployees} free</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Contractors</div>
            <div className="stat-value">{stats.totalPaidContractors}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Employees</div>
            <div className="stat-value">{stats.totalFreeEmployees}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Waitlisted</div>
            <div className="stat-value">{stats.totalWaitlisted}</div>
          </div>
        </div>

        {stats.totalPending > 0 && (
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Pending</div>
              <div className="stat-value">{stats.totalPending}</div>
            </div>
          </div>
        )}
      </div>

      {/* Revenue Breakdown Chart */}
      {stats.totalRevenue > 0 && (
        <div className="revenue-breakdown">
          <h4>Revenue by Event</h4>
          <div className="revenue-chart">
            {Object.entries(stats.revenueByEvent)
              .filter(([_, amount]) => amount > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([eventTitle, amount]) => {
                const percentage = (amount / stats.totalRevenue) * 100;
                return (
                  <div key={eventTitle} className="revenue-bar-container">
                    <div className="revenue-bar-label">
                      <span className="event-title">{eventTitle}</span>
                      <span className="event-amount">${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="revenue-bar-wrapper">
                      <div 
                        className="revenue-bar" 
                        style={{ width: `${percentage}%` }}
                        title={`${percentage.toFixed(1)}%`}
                      >
                        <span className="revenue-percentage">{percentage.toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Attendance Breakdown */}
      <div className="attendance-breakdown">
        <h4>Attendance Breakdown</h4>
        <div className="attendance-chart">
          <div className="attendance-bar">
            {stats.totalPaidContractors > 0 && (
              <div 
                className="attendance-segment contractors" 
                style={{ width: `${(stats.totalPaidContractors / stats.totalAttending) * 100}%` }}
                title={`Paid Contractors: ${stats.totalPaidContractors}`}
              >
                {stats.totalPaidContractors > 2 && <span>{stats.totalPaidContractors}</span>}
              </div>
            )}
            {stats.totalFreeEmployees > 0 && (
              <div 
                className="attendance-segment employees" 
                style={{ width: `${(stats.totalFreeEmployees / stats.totalAttending) * 100}%` }}
                title={`Free Employees: ${stats.totalFreeEmployees}`}
              >
                {stats.totalFreeEmployees > 2 && <span>{stats.totalFreeEmployees}</span>}
              </div>
            )}
          </div>
          <div className="attendance-legend">
            <div className="legend-item">
              <span className="legend-color contractors"></span>
              <span>Paid Contractors ({stats.totalPaidContractors})</span>
            </div>
            <div className="legend-item">
              <span className="legend-color employees"></span>
              <span>Free Employees ({stats.totalFreeEmployees})</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
